import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import {
  CustomObjectInstancesApi,
  type InstanceItemResponse,
} from '@tourfold/libraries/tf-api-client-public-v2';

export interface RelationOption {
  label: string;
  value: string;
}

@Component({
  selector: 'tf-relation-input',
  standalone: true,
  imports: [FormsModule, SelectModule, MultiSelectModule, ButtonModule, TranslateModule],
  template: `
    <div class="relation-input">
      @if (cardinality() === 'MANY') {
        <p-multiSelect
          [inputId]="inputId()"
          [options]="allOptions()"
          optionLabel="label"
          optionValue="value"
          [placeholder]="placeholder()"
          [ngModel]="multiValue()"
          (ngModelChange)="onMultiChange($event)"
          [invalid]="invalid()"
          [filter]="true"
          [filterPlaceHolder]="'GENERAL.SEARCH' | translate"
          [showClear]="true"
          [loading]="isLoadingOptions()"
          display="chip"
          [maxSelectedLabels]="1"
          [selectedItemsLabel]="'ATTRIBUTE_RENDERERS.RELATION_INPUT.ITEMS_SELECTED' | translate"
          appendTo="body"
          class="w-full" />
      } @else {
        <p-select
          [inputId]="inputId()"
          [options]="allOptions()"
          optionLabel="label"
          optionValue="value"
          [placeholder]="placeholder()"
          [ngModel]="singleValue()"
          (ngModelChange)="valueChange.emit($event)"
          [invalid]="invalid()"
          [filter]="true"
          [filterPlaceholder]="'GENERAL.SEARCH' | translate"
          [showClear]="true"
          [loading]="isLoadingOptions()"
          appendTo="body"
          class="w-full" />
      }
      <p-button
        severity="secondary"
        [outlined]="true"
        icon="pi pi-plus"
        [label]="'New ' + targetDefinitionTitle()"
        (onClick)="createNewRequested.emit()"
        styleClass="relation-input__new-btn" />
    </div>
  `,
  styleUrl: './relation-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // None encapsulation is required to override PrimeNG's internal `.p-multiselect-label`
  // class so chips wrap onto multiple lines instead of overflowing horizontally. Every
  // selector in the stylesheet is prefixed with `.relation-input` to keep the override
  // scoped to this component's rendered DOM.
  encapsulation: ViewEncapsulation.None,
})
export class RelationInputComponent {
  private readonly instancesApi = inject(CustomObjectInstancesApi);

  readonly value = input<string | string[] | null>(null);
  readonly targetDefinitionSlug = input<string>('');
  readonly targetDefinitionTitle = input<string>('');
  /** When a new related instance is created, pass its label here so it appears in the dropdown. */
  readonly chipLabel = input<string>('');
  readonly isNewlyCreated = input<boolean>(false);
  readonly placeholder = input<string>('');
  readonly inputId = input<string>('tf-relation-input');
  readonly invalid = input<boolean>(false);
  /** 'ONE' = single-select dropdown, 'MANY' = multi-select with chip display. */
  readonly cardinality = input<'ONE' | 'MANY'>('ONE');

  @Output() readonly valueChange = new EventEmitter<string | string[]>();
  @Output() readonly createNewRequested = new EventEmitter<void>();
  @Output() readonly removeRequested = new EventEmitter<void>();

  readonly loadedOptions = signal<RelationOption[]>([]);
  readonly isLoadingOptions = signal(false);

  /**
   * The current value coerced to the shape the single-select expects. An empty string maps to
   * `null` so the control reads as "nothing selected" — otherwise PrimeNG treats `''` as filled
   * and renders the clear (X) icon in the initial empty state (TF-1925).
   */
  readonly singleValue = computed<string | null>(() => {
    const v = this.value();
    return typeof v === 'string' && v !== '' ? v : null;
  });

  /** The current value coerced to the shape the multi-select expects. */
  readonly multiValue = computed<string[]>(() => {
    const v = this.value();
    if (Array.isArray(v)) return v;
    return typeof v === 'string' && v ? [v] : [];
  });

  onMultiChange(ids: string[] | null): void {
    this.valueChange.emit(ids ?? []);
  }

  /**
   * Merge loaded options with a newly-created instance option (if any)
   * so the dropdown always shows the selected value. Works for both single
   * (`value` is a string) and multi (`value` is a string[]) modes — the
   * caller-supplied `chipLabel` represents the most-recently-created instance.
   */
  readonly allOptions = computed(() => {
    const loaded = this.loadedOptions();
    const v = this.value();
    const label = this.chipLabel();
    if (!label) return loaded;

    const candidateId = Array.isArray(v) ? v.find((id) => !loaded.some((o) => o.value === id)) : v;
    if (!candidateId || loaded.some((o) => o.value === candidateId)) return loaded;

    const newLabel = this.isNewlyCreated() ? `${label} · new` : label;
    return [{ label: newLabel, value: candidateId }, ...loaded];
  });

  constructor() {
    effect(() => {
      const slug = this.targetDefinitionSlug();
      if (slug) {
        this.loadOptions(slug);
      }
    });
  }

  private async loadOptions(definitionSlug: string): Promise<void> {
    this.isLoadingOptions.set(true);
    try {
      const result = await this.instancesApi.listInstances({
        definitionSlug,
        size: 100,
      });
      const items = result.items ?? [];
      this.loadedOptions.set(items.map((item) => this.mapInstanceToOption(item)));
    } catch {
      this.loadedOptions.set([]);
    } finally {
      this.isLoadingOptions.set(false);
    }
  }

  private mapInstanceToOption(item: InstanceItemResponse): RelationOption {
    const data = (item.data as Record<string, unknown>) ?? {};
    const id = (data['id'] as string) ?? '';
    // Prefer the backend-resolved display value (definition's display template). Explicit
    // empty-string checks (not `||`/`??`) so a blank display value falls through to the id.
    const display = item.displayValue?.trim() ?? '';
    const label = display !== '' ? display : id !== '' ? id : '—';
    return { label, value: id };
  }
}
